import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { PhotoIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button } from '../forms/Buttons/Button';
import { toast } from 'react-toastify';
import SimpleLoader from '../Loader/SimpleLoader';
import {
  handleImageUpload,
  attachResizeHandlesToExistingImages,
  placeCaretAtPoint,
  settlePastedImages,
  uploadImageFile,
} from './EditableUtils';

// The initial HTML is not trusted (docx text reaches it unescaped): drop scripts and event handlers.
// data-* attributes (image references) and the editor's contenteditable=false wrappers are kept.
const SANITIZE_CONFIG = { ADD_ATTR: ['contenteditable'] };
// Picture sizes offered on click, as a share of the text's width (so they fit any column).
const IMAGE_SIZES = [25, 50, 75, 100];

/**
 * `sizeClassName` replaces the default one-line height, e.g. for a long passage.
 * `richImages`: pictures can also be pasted (Ctrl+V) or dropped in (uploaded on the way; Word's local
 * copies are taken out with a notice), and clicking a picture offers its size and a remove button.
 * `hideImageButton` leaves the picture button to the parent, which opens the file picker through the
 * ref's `pickImage()`.
 */
const ShortEditableContent = forwardRef(function ShortEditableContent(
  {
    placeholder,
    onChange,
    onBlur,
    value,
    ariaLabel,
    onUploadingChange,
    sizeClassName = 'min-h-[2.5rem]',
    richImages = false,
    hideImageButton = false,
  },
  ref
) {
  const [t] = useTranslation();
  const rootRef = useRef(null);
  const editorRef = useRef(null);
  const fileRef = useRef();
  const [isUploading, setIsUploading] = useState(false);
  const [picked, setPicked] = useState(null); // {wrapper, top, left}: the picture whose size is being chosen

  useImperativeHandle(ref, () => ({ pickImage: () => fileRef.current?.click() }), []);

  const defaultContent = useMemo(() => {
    return typeof value === 'string'
      ? DOMPurify.sanitize(value, SANITIZE_CONFIG)
      : value ?? <span className="text-slate-400">{placeholder}</span>;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // only empty array as dependency to avoid re-rendering

  useEffect(() => {
    attachResizeHandlesToExistingImages(editorRef, onChange);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Lets a parent hold back while an image upload is in flight.
  useEffect(() => {
    if (!isUploading || !onUploadingChange) return undefined;
    onUploadingChange(true);
    return () => onUploadingChange(false);
  }, [isUploading]); // eslint-disable-line react-hooks/exhaustive-deps

  // A click anywhere else closes the size buttons.
  useEffect(() => {
    if (!picked) return undefined;
    const onDown = (e) => {
      if (!rootRef.current?.contains(e.target)) setPicked(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [picked]);

  // The size buttons sit on the picture's top-left corner, kept inside the visible part of the field; a
  // picture scrolled out of the field (or removed) closes them.
  const pick = (wrapper) => {
    const root = rootRef.current;
    const editor = editorRef.current;
    if (!root || !editor || !wrapper || !editor.contains(wrapper)) {
      setPicked(null);
      return;
    }
    const box = wrapper.getBoundingClientRect();
    const view = editor.getBoundingClientRect();
    if (box.bottom < view.top || box.top > view.bottom) {
      setPicked(null);
      return;
    }
    const base = root.getBoundingClientRect();
    setPicked({ wrapper, top: Math.max(box.top, view.top) - base.top, left: box.left - base.left });
  };

  const setSize = (percent) => {
    const { wrapper } = picked;
    const img = wrapper.querySelector('img');
    wrapper.style.width = `${percent}%`;
    if (img) {
      img.style.width = '100%';
      img.style.height = 'auto';
    }
    onChange(editorRef.current.innerHTML);
    requestAnimationFrame(() => pick(wrapper));
  };

  const removePicked = () => {
    picked.wrapper.remove();
    setPicked(null);
    onChange(editorRef.current.innerHTML);
  };

  const uploadFiles = async (files) => {
    setIsUploading(true);
    for (const file of files) {
      await uploadImageFile(file, editorRef, onChange);
    }
    setIsUploading(false);
  };

  // Pictures the browser pasted or dropped in with text get uploaded (or taken out) right after.
  const settle = async () => {
    if (!editorRef.current?.querySelector('img:not([data-ref-key])')) return;
    setIsUploading(true);
    const dropped = await settlePastedImages(editorRef, onChange);
    setIsUploading(false);
    if (dropped) toast.warn(t('my_questions.editor.images_dropped', { count: dropped }));
  };

  const imageFiles = (list) => [...(list || [])].filter((file) => file.type.startsWith('image/'));

  const handlePaste = (e) => {
    if (!richImages) return;
    const files = imageFiles(e.clipboardData?.files);
    // A copied picture alone: upload the file itself (its HTML, if any, only points to a local copy).
    if (files.length && !e.clipboardData.getData('text/plain').trim()) {
      e.preventDefault();
      uploadFiles(files);
      return;
    }
    setTimeout(settle, 0);
  };

  const handleDrop = (e) => {
    if (!richImages) return;
    const files = imageFiles(e.dataTransfer?.files);
    if (!files.length) {
      setTimeout(settle, 0);
      return;
    }
    e.preventDefault();
    placeCaretAtPoint(e.clientX, e.clientY);
    uploadFiles(files);
  };

  const current = picked?.wrapper.style.width;

  return (
    <div ref={rootRef} className="relative flex flex-row-reverse items-start gap-2">
      <style>{`
        .resize-handle {
          display: none;
        }
        .img-wrapper {
          border: 1px solid transparent;
        }
        div[contenteditable="true"]:focus-within .resize-handle {
          display: block;
        }
        div[contenteditable="true"]:focus-within .img-wrapper {
          border: 1px solid #cbd5e1;
        }
  `}</style>

      <input
        type="file"
        accept="image/*"
        ref={fileRef}
        id="image-upload"
        style={{ display: 'none' }}
        onChange={(e) =>
          handleImageUpload(e, editorRef, setIsUploading, onChange)
        }
      />
      {!hideImageButton && (
        <Button
          type="button"
          color="secondary-outline"
          className="w-10 shrink-0 !px-0"
          aria-label={t('my_questions.editor.upload_image')}
          onClick={() => fileRef.current.click()}
          disabled={isUploading}
        >
          {isUploading ? (
            <SimpleLoader className="h-5" />
          ) : (
            <PhotoIcon className="h-5" />
          )}
        </Button>
      )}
      <div
        ref={editorRef}
        className={`${sizeClassName} w-full min-w-0 cursor-text rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm transition duration-200 hover:border-slate-400 focus:border-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-600/15`}
        contentEditable
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel}
        dangerouslySetInnerHTML={{ __html: defaultContent }}
        onInput={(e) => {
          onChange(e.target.innerHTML);
        }}
        onFocus={(e) => {
          if (e.target.innerText.startsWith(placeholder) && placeholder) {
            e.target.innerText = '';
          }
        }}
        onBlur={(e) => {
          if (['', '\n'].includes(e.target.innerText) && placeholder) {
            e.target.innerHTML = `<span class="text-slate-400">${placeholder}</span>`;
          }
          onBlur?.(e);
        }}
        onClick={richImages ? (e) => pick(e.target.closest?.('.img-wrapper')) : undefined}
        onKeyDown={richImages && picked ? () => setPicked(null) : undefined}
        onScroll={richImages && picked ? () => pick(picked.wrapper) : undefined}
        onPaste={handlePaste}
        onDragOver={richImages ? (e) => Array.from(e.dataTransfer?.types || []).includes('Files') && e.preventDefault() : undefined}
        onDrop={handleDrop}
      />
      {picked && (
        <div
          role="toolbar"
          aria-label={t('my_questions.editor.image_size')}
          style={{ top: picked.top + 4, left: picked.left + 4 }}
          className="absolute z-10 flex items-center gap-0.5 rounded-lg bg-white/95 p-1 text-xs font-medium shadow-md ring-1 ring-slate-200"
        >
          {IMAGE_SIZES.map((percent) => (
            <button
              key={percent}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setSize(percent)}
              aria-pressed={current === `${percent}%`}
              aria-label={t('my_questions.editor.image_size_option', { percent })}
              className={`rounded-md px-2 py-1 tabular-nums transition duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 ${
                current === `${percent}%` ? 'bg-blue-600 text-white' : 'text-slate-700 hover:bg-slate-100'
              }`}
            >
              {percent}%
            </button>
          ))}
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={removePicked}
            aria-label={t('my_questions.editor.remove_image')}
            title={t('my_questions.editor.remove_image')}
            className="ml-0.5 rounded-md p-1 text-slate-500 transition duration-200 hover:bg-red-50 hover:text-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600"
          >
            <TrashIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
});

export default ShortEditableContent;
